import { connect, injectReducer, injectSaga } from '@vfde-sails/core';
import saga from './saga';
import reducer from './reducer';
import * as mapDispatch from './action';
import { selectors as mapStateToProps } from './selector';
import tariffSelection from 'Container/TariffSelection';
import availabilityCheck from 'Container/AvailabilityCheck';
import priceSection from 'Container/PriceSection';
import { mountLoadingSpinner, updateLoadingSpinner } from '../../components/loadingSpinner';
import {
    AddressHtmlId,
    AppHtmlId,
} from '../../enums/elementId';
import { TrackingAvcStep } from '../../enums/tracking';
import { APP_NAMESPACE } from './constants';
import { getUpdateFlags, loadInitialAppState, saveStateToSession } from './util/state';
import { updateOverlayContent } from '../../components/overlayContent';
import { mountOverlayComponent } from '../../components/overlay';
import { AccordionType, mountAccordion, updateAccordionFootnote } from '../../components/accordion';
import { mountAllTabs } from '../../components/tabs';
import { mountRelatedLinks } from '../../components/relatedLinks';
import { mountNotificationServiceError } from '../../components/notification';
import { showElement } from '../../util/visibility';
import { mountText, updateAddressText } from '../../components/justText';
import { trackPageView } from '../../util/tracking';
import { Accordion } from '@vfde-brix/ws10/accordion';
import { HtmlSelector } from '../../constants';
import { setAvcResultSession } from '@vfde-sails/avc-core';
import { ProductType } from '../../enums/product';
import { debugInfoProd } from '../../util/debug';
import isCmsConfigValid from '../../util/cmsValidator';
import { mountPromotionalCard, updatePromotionalCard } from '../../components/promotionalCard';
import { mountLinearStepper } from '../../components/linearStepper';

export type State = ReturnType<typeof mapStateToProps>;
export type Actions = typeof mapDispatch;

function appContainer(_: State, actions: Actions) {
    injectSaga(APP_NAMESPACE, saga);
    injectReducer(APP_NAMESPACE, reducer);

    if (!isCmsConfigValid()) {
        actions.setError(new Error('CMS Config is invalid!'));
        debugInfoProd('CMS Config is invalid!');
    }

    const loadedState = loadInitialAppState();

    tariffSelection();
    priceSection();

    const loadingSpinner = mountLoadingSpinner(AppHtmlId.LOADING);
    const addressText = mountText(AddressHtmlId.ADDRESS_TEXT);
    const footnote = mountAccordion(AppHtmlId.DYNAMIC_FOOTNOTE, AccordionType.Footnote);
    mountNotificationServiceError(AppHtmlId.ERROR, HtmlSelector.PDP_ERROR_HOTLINE);

    mountOverlayComponent(actions.closeOverlay);
    mountLinearStepper();
    showElement(AppHtmlId.LINEAR_STEPPER, true);


    const promotionCard = mountPromotionalCard(AppHtmlId.FIXNET_WHOLYBUY_CARD);

    // Static content area: start
    mountAllTabs();
    mountAccordion(AppHtmlId.ACCORDION_FAQ, AccordionType.FAQ);
    mountAccordion(AppHtmlId.ACCORDION_COMISSION, AccordionType.Comission);
    mountAccordion(AppHtmlId.ACCORDION_CONNECTION, AccordionType.Connection);
    mountAccordion(AppHtmlId.ACCORDION_COST, AccordionType.Cost);
    mountRelatedLinks(AppHtmlId.RELATED_LINKS_OFFERS);
    mountRelatedLinks(AppHtmlId.RELATED_LINKS_GENERAL);
    // Static content area: end

    actions.initStore(loadedState);

    return {
        getDerivedStateFromProps(newState: State, oldState: State) {
            const {
                activeOverlay,
                isContentEnabled,
                avcResult,
                selectedTariff,
                tariffs,
                isBackendDown,
                homesPassed,
                accessSupplier,
                availabilityIndicator,
            } = newState;
            const update = getUpdateFlags(newState, oldState);
            if (update.overlay) {
                availabilityCheck();
            };
            update.overlayChanged && updateOverlayContent(activeOverlay);
            update.avcOpened && trackPageView(
                selectedTariff!,
                undefined,
                TrackingAvcStep.SHOW,
            );

            if (update.avcResultChanged) {
                showElement(AddressHtmlId.ADDRESS_WRAPPER, Boolean(avcResult));
                updateAddressText(addressText, avcResult?.address);

                // Persist AVC result in session storage
                avcResult && setAvcResultSession(
                    {
                        ...avcResult,
                        accessSupplier,
                        availabilityIndicator,
                    },
                    ProductType.PRE_FIBER,
                    [
                        tariffs,
                        homesPassed,
                    ],
                );
            };

            update.footnote && updateAccordionFootnote(
                footnote as Accordion,
                tariffs,
                selectedTariff,
                avcResult?.selected?.regioCharge,
                avcResult?.availability.fiber,
                isContentEnabled,
            );
            update.isLoading && updateLoadingSpinner(loadingSpinner, newState.isLoading);

            showElement(AppHtmlId.APP_CONTAINER, !isBackendDown);
            showElement(AppHtmlId.ERROR, isBackendDown);

            saveStateToSession(newState.appState);

            update.promotionalCard && updatePromotionalCard(
                promotionCard,
                accessSupplier,
                availabilityIndicator,
                homesPassed
            );
        },
    };
}

const Container = connect(mapStateToProps, mapDispatch)(appContainer);

export default Container;
